// src/practices/FlightBooking.js
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { Container, Row, Col, Form, Button, Card, ListGroup, Modal, Navbar } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import HeroImage from '../icons/flight.jpg';
import TesterBudNavbar from '../../subComponent/TesterBudNavBar';

const FlightBookingAutomation = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [departureDate, setDepartureDate] = useState('');
  const [returnDate, setReturnDate] = useState('');
  const [passengers, setPassengers] = useState(1);
  const [travelClass, setTravelClass] = useState('Economy');
  const [oneWay, setOneWay] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedDepartureFlight, setSelectedDepartureFlight] = useState(null);
  const [selectedReturnFlight, setSelectedReturnFlight] = useState(null);
  const [paymentDetails, setPaymentDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
  });
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const cities = ['New York', 'London', 'Tokyo', 'Paris', 'Sydney', 'Mumbai', 'Delhi', 'Dubai', 'Singapore'];

  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    if (!from) {
      newErrors.from = 'Please select a departure city.';
      isValid = false;
    }

    if (!to) {
      newErrors.to = 'Please select a destination city.';
      isValid = false;
    }

    if (from && to && from === to) {
      newErrors.to = 'Departure and destination cities cannot be the same.';
      isValid = false;
    }

    if (!departureDate) {
      newErrors.departureDate = 'Please select a departure date.';
      isValid = false;
    }

    if (!oneWay && !returnDate) {
      newErrors.returnDate = 'Please select a return date.';
      isValid = false;
    }

    const today = new Date().toISOString().split('T')[0];
  if (departureDate < today) {
    newErrors.departureDate = 'Departure date cannot be in the past.';
    isValid = false;
  }

    if (!oneWay && returnDate <= departureDate) {
      newErrors.returnDate = 'Return date must be after departure date.';
      isValid = false;
    }

    if (passengers < 1) {
      newErrors.passengers = 'Passengers must be at least 1.';
      isValid = false;
    }

    if (!travelClass) {
      newErrors.travelClass = 'Please select a travel class.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSearch = () => {
    if (validateForm()) {
      // Simulate API call with a slight delay for a more realistic feel
      setTimeout(() => {
        const departureFlights = [
          {
            id: 1,
            type: 'departure',
            from,
            to,
            departureDate,
            price: Math.floor(Math.random() * 500) + 150,
            airline: 'Global Wings',
            flightNumber: 'GW205',
            departureTime: '08:00',
            arrivalTime: '12:00',
            duration: '4h 0m',
          },
          {
            id: 2,
            type: 'departure',
            from,
            to,
            departureDate,
            price: Math.floor(Math.random() * 450) + 120,
            airline: 'Sky High Airlines',
            flightNumber: 'SH412',
            departureTime: '14:30',
            arrivalTime: '18:15',
            duration: '3h 45m',
          },
        ];

        const returnFlights = !oneWay && returnDate ? [
          {
            id: 3,
            type: 'return',
            from: to,
            to: from,
            departureDate: returnDate,
            price: Math.floor(Math.random() * 550) + 180,
            airline: 'Global Wings',
            flightNumber: 'GW206',
            departureTime: '16:00',
            arrivalTime: '20:00',
            duration: '4h 0m',
          },
          {
            id: 4,
            type: 'return',
            from: to,
            to: from,
            departureDate: returnDate,
            price: Math.floor(Math.random() * 500) + 140,
            airline: 'Air Swift',
            flightNumber: 'AS790',
            departureTime: '20:00',
            arrivalTime: '23:30',
            duration: '3h 30m',
          },
        ] : [];

        setSearchResults([...departureFlights, ...returnFlights]);
        setSelectedDepartureFlight(null);
        setSelectedReturnFlight(null);
        setBookingSuccess(false);
        setErrors({});
      }, 1000); // Simulate 1 second loading
    }
  };

  const handleSelectFlight = (flight) => {
    if (flight.type === 'departure') {
      setSelectedDepartureFlight(flight);
      // Show payment modal if it's a one-way trip after selecting departure
      if (oneWay) {
        setShowPaymentModal(true);
      }
    } else if (flight.type === 'return') {
      setSelectedReturnFlight(flight);
      // Show payment modal if it's a round trip and both flights are selected
      if (!oneWay && selectedDepartureFlight) {
        setShowPaymentModal(true);
      }
    }
  };

  const isDepartureFlightSelected = (flight) => selectedDepartureFlight && selectedDepartureFlight.id === flight.id;
  const isReturnFlightSelected = (flight) => selectedReturnFlight && selectedReturnFlight.id === flight.id;
  

  const handlePaymentChange = (e) => {
    setPaymentDetails({ ...paymentDetails, [e.target.name]: e.target.value });
  };

  const handleBookFlight = () => {
    if (oneWay && selectedDepartureFlight && paymentDetails.cardNumber && paymentDetails.expiryDate && paymentDetails.cvv) {
      setBookingSuccess(true);
      setBookingDetails({
        flight: selectedDepartureFlight,
        returnFlight: null,
        payment: {
          last4Digits: paymentDetails.cardNumber.slice(-4),
          expiry: paymentDetails.expiryDate,
        },
      });
      setSelectedDepartureFlight(null);
      setSelectedReturnFlight(null);
      setPaymentDetails({ cardNumber: '', expiryDate: '', cvv: '' });
      setSearchResults([]);
      setErrors({});
      setShowPaymentModal(false);
    } else if (!oneWay && selectedDepartureFlight && selectedReturnFlight && paymentDetails.cardNumber && paymentDetails.expiryDate && paymentDetails.cvv) {
      setBookingSuccess(true);
      setBookingDetails({
        flight: selectedDepartureFlight,
        returnFlight: selectedReturnFlight,
        payment: {
          last4Digits: paymentDetails.cardNumber.slice(-4),
          expiry: paymentDetails.expiryDate,
        },
      });
      setSelectedDepartureFlight(null);
      setSelectedReturnFlight(null);
      setPaymentDetails({ cardNumber: '', expiryDate: '', cvv: '' });
      setSearchResults([]);
      setErrors({});
      setShowPaymentModal(false);
    } else {
      alert('Please select both departure and return flights and enter complete payment details.');
    }
  };

  const handleClosePaymentModal = () => setShowPaymentModal(false);

  const hasSelectedFlights = oneWay ? selectedDepartureFlight : (selectedDepartureFlight && selectedReturnFlight);

  return (
    <HelmetProvider>
      <Helmet>
        <title>TesterBud: Dummy Flight Booking Application</title>
      </Helmet>

      <Container fluid className="hero-section bg-primary text-white py-4" style={{ position: 'relative', overflow: 'hidden' }}>
          <Row className="align-items-center justify-content-center">
            
            <Col md={6} className="text-center text-md-start">
            <h1 className="display-4 fw-bold mb-3">TesterBud: Dummy Flight Booking Application </h1>
              
              <p className="lead mb-4">
              Learn to automate flight booking processes, including selecting travel options and verifying booking confirmations. 
              </p><p className="lead mb-4">
              Practise, Create Test case, Automate it and become more confident Frontend automation expert. 🚀
              </p>
              

            </Col>
            <Col md={5} className="d-none d-md-block text-center">
              {/* Replace with your actual hero image component or import */}
              <div style={{ width: '65%', height: '65%', display: 'flex', justifyContent: 'flex-end', alignItems: 'flex-start' }}>
                <div
                  style={{
                    width: '50%', // Adjust the size of the circular area
                    height: '50%', // Ensure width and height are equal for a perfect circle
                    borderRadius: '70%', // This creates the circular shape
                    overflow: 'auto', // Clips the image to the circle
                    marginTop: '-4%',
                    marginRight: '10%',
                    marginBottom:'-4%',
                  }}
                >
                  <img src={HeroImage} alt="Automation Illustration" className="img-fluid" style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain', // Or 'cover' depending on desired behavior
                  }} />
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <TesterBudNavbar/>
        <Navbar bg="dark" variant="dark" expand="lg" className="mb-4">
  <Container>
    <Navbar.Brand as={Link} to="/" className="fw-bold me-3">
      TesterBud: Premium Flight Booking Application
    </Navbar.Brand>

    

    
  </Container>
</Navbar>

    <Container className="pb-5">
      <Container className="py-5" style={{ background: 'linear-gradient(...)' }}>
        <Card className="shadow-lg rounded-lg p-4">
          <Card.Body>
            
            <h2 className="text-primary mb-4">Find Your Perfect Flight</h2>
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="from">
                    <Form.Label>From:</Form.Label>
                    <Form.Select value={from} onChange={(e) => setFrom(e.target.value)}>
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.from && <Form.Text className="text-danger">{errors.from}</Form.Text>}
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="to">
                    <Form.Label>To:</Form.Label>
                    <Form.Select value={to} onChange={(e) => setTo(e.target.value)}>
                      <option value="">Select City</option>
                      {cities.map((city) => (
                        <option key={city} value={city}>
                          {city}
                        </option>
                      ))}
                    </Form.Select>
                    {errors.to && <Form.Text className="text-danger">{errors.to}</Form.Text>}
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="departureDate">
                    <Form.Label>Departure Date:</Form.Label>
                    <Form.Control
                      type="date"
                      value={departureDate}
                      onChange={(e) => setDepartureDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                    />
                    {errors.departureDate && (
                      <Form.Text className="text-danger">{errors.departureDate}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
                {!oneWay && (
                  <Col md={6}>
                    <Form.Group controlId="returnDate">
                      <Form.Label>Return Date:</Form.Label>
                      <Form.Control
                        type="date"
                        value={returnDate}
                        onChange={(e) => setReturnDate(e.target.value)}
                        min={departureDate || new Date().toISOString().split('T')[0]}
                      />
                      {errors.returnDate && (
                        <Form.Text className="text-danger">{errors.returnDate}</Form.Text>
                      )}
                    </Form.Group>
                  </Col>
                )}
              </Row>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Group controlId="passengers">
                    <Form.Label>Passengers:</Form.Label>
                    <Form.Control
                      type="number"
                      value={passengers}
                      onChange={(e) => setPassengers(parseInt(e.target.value))}
                      min="1"
                    />
                    {errors.passengers && (
                      <Form.Text className="text-danger">{errors.passengers}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group controlId="travelClass">
                    <Form.Label>Travel Class:</Form.Label>
                    <Form.Select value={travelClass} onChange={(e) => setTravelClass(e.target.value)}>
                      <option value="Economy">Economy</option>
                      <option value="Business">Business</option>
                      <option value="First">First</option>
                    </Form.Select>
                    {errors.travelClass && (
                      <Form.Text className="text-danger">{errors.travelClass}</Form.Text>
                    )}
                  </Form.Group>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                  <Form.Check
                    type="checkbox"
                    id="oneWay"
                    label="One Way"
                    checked={oneWay}
                    onChange={() => setOneWay(!oneWay)}
                  />
                </Col>
              </Row>
              <Button variant="primary" onClick={handleSearch} className="w-100">
                Search Flights
              </Button>
            </Form>
          </Card.Body>
        </Card>

        {searchResults.length > 0 && (
          <Card className="shadow-lg rounded-lg mt-4">
            <Card.Body>
              <h3 className="text-info mb-3">Available Flights</h3>
              <ListGroup variant="flush">
                {searchResults.map((result) => (
                  <ListGroup.Item
                    key={result.id}
                    action
                    onClick={() => handleSelectFlight(result)}
                    className={`d-flex justify-content-between align-items-center ${(result.type === 'departure' && isDepartureFlightSelected(result)) || (result.type === 'return' && isReturnFlightSelected(result)) ? 'active' : ''
                      }`}
                    style={{ cursor: 'pointer' }}
                  >
                    <div>
                      <h6 className="mb-1">
                        <strong>{result.airline}</strong> ({result.flightNumber}) - {result.type === 'departure' ? 'Departure' : 'Return'}
                      </h6>
                      <p className="mb-0 text-muted">
                        {result.from} ({result.departureTime}) - {result.to} ({result.arrivalTime})
                      </p>
                      <small className="text-muted">
                        {new Date(result.departureDate).toLocaleDateString()}
                        {result.type === 'return' && ` - ${new Date(result.departureDate).toLocaleDateString()}`}
                      </small>
                      <small className="text-muted d-block">Duration: {result.duration}</small>
                    </div>
                    <span className="font-weight-bold text-success">${result.price}</span>
                  </ListGroup.Item>
                ))}
              </ListGroup>
            </Card.Body>
          </Card>
        )}

        {searchResults.length === 0 && from && to && departureDate && (
          <Card className="shadow-sm rounded-lg mt-4">
            <Card.Body className="text-center">
              <p className="text-muted">No flights found for the selected criteria.</p>
            </Card.Body>
          </Card>
        )}

        <Modal show={showPaymentModal} onHide={handleClosePaymentModal}>
          <Modal.Header closeButton>
            <Modal.Title>Payment Details</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedDepartureFlight && (
              <div className="mb-3">
                <h5>Selected Departure Flight</h5>
                <p>
                  <strong>{selectedDepartureFlight.airline}</strong> ({selectedDepartureFlight.flightNumber}): {selectedDepartureFlight.from} to{' '}
                  {selectedDepartureFlight.to}
                </p>
                <p>Departure: {new Date(selectedDepartureFlight.departureDate).toLocaleDateString()}</p>
                <p>Price: <span className="font-weight-bold text-success">${selectedDepartureFlight.price}</span></p>
              </div>
            )}
            {!oneWay && selectedReturnFlight && (
              <div className="mb-3">
                <h5>Selected Return Flight</h5>
                <p>
                  <strong>{selectedReturnFlight.airline}</strong> ({selectedReturnFlight.flightNumber}): {selectedReturnFlight.from} to{' '}
                  {selectedReturnFlight.to}
                </p>
                <p>Departure: {new Date(selectedReturnFlight.departureDate).toLocaleDateString()}</p>
                <p>Price: <span className="font-weight-bold text-success">${selectedReturnFlight.price}</span></p>
              </div>
            )}
            <Form>
              <Form.Group className="mb-3" controlId="cardNumber">
                <Form.Label>Card Number:</Form.Label>
                <Form.Control
                  type="text"
                  name="cardNumber"
                  value={paymentDetails.cardNumber}
                  onChange={handlePaymentChange}
                />
              </Form.Group>
              <Row className="mb-3">
                <Col md={6}>
                  <Form.Group controlId="expiryDate">
                    <Form.Label>Expiry Date:</Form.Label>
                    <Form.Control
                      type="text"
                      name="expiryDate"
                      placeholder="MM/YY"
                      value={paymentDetails.expiryDate}
                      onChange={handlePaymentChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="cvv">
                    <Form.Label>CVV:</Form.Label>
                    <Form.Control
                      type="text"
                      name="cvv"
                      value={paymentDetails.cvv}
                      onChange={handlePaymentChange}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Button variant="success" onClick={handleBookFlight} className="w-100" disabled={!hasSelectedFlights}>
                Book Flight
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {bookingSuccess && bookingDetails && (
          <Card className="shadow-lg rounded-lg mt-4">
            <Card.Body className="text-center">
              <h4 className="text-success mb-3">Booking Successful!</h4>
              <p>
                Your departure flight from <strong>{bookingDetails.flight.from}</strong> to{' '}
                <strong>{bookingDetails.flight.to}</strong> has been booked successfully.
              </p>
              <p>
                Departure Date: {new Date(bookingDetails.flight.departureDate).toLocaleDateString()}
              </p>
              {bookingDetails.returnFlight && (
                <p>
                  Your return flight from <strong>{bookingDetails.returnFlight.from}</strong> to{' '}
                  <strong>{bookingDetails.returnFlight.to}</strong> has also been booked.
                </p>
              )}
              {bookingDetails.returnFlight && (
                <p>
                  Return Date: {new Date(bookingDetails.returnFlight.departureDate).toLocaleDateString()}
                </p>
              )}
              <p>
                Total Price: <span className="font-weight-bold text-success">${oneWay ? bookingDetails.flight.price : (bookingDetails.flight.price + bookingDetails.returnFlight.price)}</span>
              </p>
              <p className="text-muted">
                Payment made with card ending in: ****-****-****-{bookingDetails.payment.last4Digits} (Expiry:{' '}
                {bookingDetails.payment.expiry})
              </p>
            </Card.Body>
          </Card>
        )}
        <div className="text-center mt-4">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Button variant="outline-secondary">Back To Home Page ➡️</Button>
          </Link>
        </div>
      </Container>
      </Container>
      <footer className="bg-dark text-light text-center py-4 mt-auto">
        <Container>
          <p className="mb-1">Ready to start automating? Explore, practice, and master!</p>
          <small>© 2025 TesterBud | Built for learners by learners 🚀</small>
        </Container>
      </footer>
    </HelmetProvider>
  );
};

export default FlightBookingAutomation;